/**
 * YouTube Data API v3 – search for videos (used in Live Song Request to find and fill song link + title).
 * Requires VITE_YOUTUBE_API_KEY in environment.
 */

const YOUTUBE_API_BASE = 'https://www.googleapis.com/youtube/v3';

export interface YouTubeSearchItem {
  videoId: string;
  title: string;
  channelTitle: string;
  thumbnailUrl: string;
}

function getApiKey(): string {
  const key = import.meta.env.VITE_YOUTUBE_API_KEY;
  if (!key || typeof key !== 'string') {
    throw new Error('VITE_YOUTUBE_API_KEY is not set. Add it to your .env file.');
  }
  return key;
}

/**
 * Search YouTube for videos. Returns list of items with videoId, title, channelTitle, thumbnailUrl.
 */
export async function searchYouTubeVideos(query: string, maxResults: number = 10): Promise<YouTubeSearchItem[]> {
  const trimmed = (query || '').trim();
  if (!trimmed) return [];

  const key = getApiKey();
  const params = new URLSearchParams({
    part: 'snippet',
    type: 'video',
    maxResults: String(Math.min(Math.max(maxResults, 1), 50)),
    q: trimmed,
    key,
  });

  const url = `${YOUTUBE_API_BASE}/search?${params.toString()}`;
  const res = await fetch(url);
  if (!res.ok) {
    const errText = await res.text();
    throw new Error(res.status === 403 ? 'YouTube API quota or key issue. Check VITE_YOUTUBE_API_KEY.' : errText || `YouTube API error: ${res.status}`);
  }
  const data = await res.json();
  const items = data?.items ?? [];
  return items
    .filter((i: any) => i?.id?.videoId && i?.snippet)
    .map((i: any) => ({
      videoId: i.id.videoId,
      title: i.snippet.title ?? '',
      channelTitle: i.snippet.channelTitle ?? '',
      thumbnailUrl: i.snippet.thumbnails?.default?.url ?? i.snippet.thumbnails?.medium?.url ?? '',
    }));
}

export function getYouTubeWatchUrl(videoId: string): string {
  return `https://www.youtube.com/watch?v=${videoId}`;
}

export function getYouTubeEmbedUrl(videoId: string): string {
  return `https://www.youtube.com/embed/${videoId}`;
}
